// Per-dialect synthesizer for CREATE / DROP TYPE + DOMAIN (v2 Slice D4).
//
// PRD: Ralph/DdlStructuralEdit2/PRD.md § Slice D4.
//
// Per-engine surface:
//   - mssql:  alias  → CREATE TYPE [schema.]name FROM <baseType>
//             table  → CREATE TYPE [schema.]name AS TABLE (<columns>)
//             DROP TYPE [IF EXISTS] [schema.]name
//   - pgsql:  enum   → CREATE TYPE name AS ENUM ('a', 'b', …)
//             composite → CREATE TYPE name AS (col1 type1, col2 type2, …)
//             domain → CREATE DOMAIN name AS <baseType>
//                      [DEFAULT <expr>] [NOT NULL]
//                      [CONSTRAINT <name>] CHECK (<expr>) [, …]
//             range  → CREATE TYPE name AS RANGE
//                      (subtype = …, [subtype_opclass = …, collation = …,
//                      canonical = …, subtype_diff = …])
//             DROP {TYPE|DOMAIN} [IF EXISTS] name [CASCADE]
//   - mysql:  unsupported — no user-defined type system.
//   - oracle: object → CREATE [OR REPLACE] TYPE name AS OBJECT
//                      (attr1 type1, attr2 type2, …)
//             table-of → CREATE [OR REPLACE] TYPE name AS TABLE OF <baseType>
//             varray → CREATE [OR REPLACE] TYPE name AS VARRAY(n) OF <baseType>
//             DROP TYPE name [FORCE]
//   - sqlite: unsupported — no user-defined type system.

import type { QualifiedName, SqlEngine } from '../../../sql-grammar/model';
import type { UserDefinedTypeDef, UserDefinedTypeKind } from '../../types';
import { DdlEditError } from '../../types';

import { formatColumnBody, maybeQuoteIdent } from './dialect';

function typeName(engine: SqlEngine, name: QualifiedName): string {
  if (name.raw && name.raw.length > 0) return name.raw;
  const bare = maybeQuoteIdent(engine, name.name, name.quoted);
  if (name.schema) {
    const schema = maybeQuoteIdent(engine, name.schema, name.quoted);
    return `${schema}.${bare}`;
  }
  return bare;
}

function unsupported(engine: SqlEngine, op: string, hint: string): never {
  throw new DdlEditError(
    'engine-does-not-support-op',
    `applyDdlEdit: ${engine} does not support ${op} — ${hint}`,
  );
}

function rejectKind(
  engine: SqlEngine,
  kind: UserDefinedTypeKind,
  hint: string,
): never {
  throw new DdlEditError(
    'engine-does-not-support-op',
    `applyDdlEdit: ${engine} create-type does not support kind '${kind}' — ${hint}`,
  );
}

const MYSQL_HINT
  = `mysql has no user-defined type system. Use ENUM('a','b',…) / SET(…) `
    + `column types directly via op:'add-column' for enum-like fields.`;
const SQLITE_HINT
  = `sqlite has no user-defined type system. Use CHECK constraints on a `
    + `column to restrict allowed values.`;

const MSSQL_KIND_HINTS: Record<UserDefinedTypeKind, string> = {
  alias: '',
  table: '',
  enum: `mssql has no enum type. Use a CHECK constraint or a lookup table.`,
  composite: `mssql has no composite type. Use kind:'table' (TVP) for the closest equivalent.`,
  domain: `mssql has no CREATE DOMAIN. Use kind:'alias' for the closest equivalent.`,
  range: `mssql has no range type.`,
  object: `mssql has no oracle-style object types.`,
  'table-of': `mssql has no oracle-style table-of types.`,
  varray: `mssql has no oracle-style VARRAY types.`,
};

const PGSQL_KIND_HINTS: Record<UserDefinedTypeKind, string> = {
  enum: '',
  composite: '',
  domain: '',
  range: '',
  alias: `pgsql has no CREATE TYPE FROM alias. Use kind:'domain' to wrap a base type with constraints.`,
  table: `pgsql has no TVP. Use kind:'composite' for a row-shaped type.`,
  object: `pgsql has no oracle-style object types. Use kind:'composite' for the closest equivalent.`,
  'table-of': `pgsql has no oracle-style table-of types. Use array types (e.g. INTEGER[]) directly on columns.`,
  varray: `pgsql has no oracle-style VARRAY types. Use array types (e.g. INTEGER[]) directly on columns.`,
};

const ORACLE_KIND_HINTS: Record<UserDefinedTypeKind, string> = {
  object: '',
  'table-of': '',
  varray: '',
  alias: `oracle has no CREATE TYPE FROM alias.`,
  table: `oracle has no TVP. Use kind:'object' or kind:'table-of' for nested-table types.`,
  enum: `oracle has no enum type. Use a CHECK constraint or a lookup table.`,
  composite: `oracle has no pgsql-style composite type. Use kind:'object' for an attribute-bearing type.`,
  domain: `oracle has no CREATE DOMAIN. Use SUBTYPE inside a PL/SQL package, or a CHECK constraint.`,
  range: `oracle has no range type.`,
};

// ─── CREATE TYPE / DOMAIN ─────────────────────────────────────────────────

export function synthesizeCreateUserDefinedType(
  engine: SqlEngine,
  type: UserDefinedTypeDef,
): string {
  if (engine === 'mysql') unsupported('mysql', 'create-type', MYSQL_HINT);
  if (engine === 'sqlite') unsupported('sqlite', 'create-type', SQLITE_HINT);
  if (engine === 'mssql') return synthMssql(type);
  if (engine === 'postgres') return synthPgsql(type);
  return synthOracle(type);
}

function synthMssql(type: UserDefinedTypeDef): string {
  const name = typeName('mssql', type.name);
  if (type.kind === 'alias') {
    return `CREATE TYPE ${name} FROM ${type.baseType}`;
  }
  if (type.kind === 'table') {
    const cols = type.columns
      .map((c) => formatColumnBody('mssql', c, type.name.quoted))
      .join(', ');
    return `CREATE TYPE ${name} AS TABLE (${cols})`;
  }
  rejectKind('mssql', type.kind, MSSQL_KIND_HINTS[type.kind]);
}

function synthPgsql(type: UserDefinedTypeDef): string {
  const name = typeName('postgres', type.name);
  if (type.kind === 'enum') {
    const values = type.values.map(quoteSqlString).join(', ');
    return `CREATE TYPE ${name} AS ENUM (${values})`;
  }
  if (type.kind === 'composite') {
    const attrs = type.attributes
      .map((a) => `${maybeQuoteIdent('postgres', a.name, type.name.quoted)} ${a.typeRef}`)
      .join(', ');
    return `CREATE TYPE ${name} AS (${attrs})`;
  }
  if (type.kind === 'domain') {
    let s = `CREATE DOMAIN ${name} AS ${type.baseType}`;
    if (type.defaultText !== undefined) s += ` DEFAULT ${type.defaultText}`;
    if (type.notNull) s += ' NOT NULL';
    if (type.checks) {
      for (const ck of type.checks) {
        s += ck.name
          ? ` CONSTRAINT ${maybeQuoteIdent('postgres', ck.name, false)} CHECK (${ck.expression})`
          : ` CHECK (${ck.expression})`;
      }
    }
    return s;
  }
  if (type.kind === 'range') {
    const parts: string[] = [`subtype = ${type.subtype}`];
    if (type.subtypeOpclass) parts.push(`subtype_opclass = ${type.subtypeOpclass}`);
    if (type.collation) parts.push(`collation = ${type.collation}`);
    if (type.canonical) parts.push(`canonical = ${type.canonical}`);
    if (type.subtypeDiff) parts.push(`subtype_diff = ${type.subtypeDiff}`);
    return `CREATE TYPE ${name} AS RANGE (${parts.join(', ')})`;
  }
  rejectKind('postgres', type.kind, PGSQL_KIND_HINTS[type.kind]);
}

function synthOracle(type: UserDefinedTypeDef): string {
  const name = typeName('oracle', type.name);
  if (type.kind === 'object') {
    const head = type.orReplace ? 'CREATE OR REPLACE TYPE' : 'CREATE TYPE';
    const attrs = type.attributes
      .map((a) => `${maybeQuoteIdent('oracle', a.name, type.name.quoted)} ${a.typeRef}`)
      .join(', ');
    return `${head} ${name} AS OBJECT (${attrs})`;
  }
  if (type.kind === 'table-of') {
    const head = type.orReplace ? 'CREATE OR REPLACE TYPE' : 'CREATE TYPE';
    return `${head} ${name} AS TABLE OF ${type.baseType}`;
  }
  if (type.kind === 'varray') {
    const head = type.orReplace ? 'CREATE OR REPLACE TYPE' : 'CREATE TYPE';
    return `${head} ${name} AS VARRAY(${type.size}) OF ${type.baseType}`;
  }
  rejectKind('oracle', type.kind, ORACLE_KIND_HINTS[type.kind]);
}

function quoteSqlString(s: string): string {
  return `'${s.replace(/'/g, "''")}'`;
}

// ─── DROP TYPE / DOMAIN ───────────────────────────────────────────────────

export function synthesizeDropUserDefinedType(
  engine: SqlEngine,
  name: QualifiedName,
  isDomain: boolean,
  ifExists: boolean,
  cascade: boolean,
): string {
  if (engine === 'mysql') unsupported('mysql', 'drop-type', MYSQL_HINT);
  if (engine === 'sqlite') unsupported('sqlite', 'drop-type', SQLITE_HINT);
  if (isDomain && engine !== 'postgres') {
    throw new Error(
      `applyDdlEdit: ${engine} drop-type does not accept 'isDomain' — only `
        + `pgsql has CREATE DOMAIN / DROP DOMAIN.`,
    );
  }
  if (engine === 'oracle' && ifExists) {
    throw new Error(
      `applyDdlEdit: oracle drop-type does not accept 'ifExists' — oracle's `
        + `DROP TYPE has no IF EXISTS keyword.`,
    );
  }
  const keyword = isDomain ? 'DROP DOMAIN' : 'DROP TYPE';
  const cascadeKeyword = engine === 'oracle' ? 'FORCE' : 'CASCADE';
  let s = keyword;
  if (ifExists) s += ' IF EXISTS';
  s += ` ${typeName(engine, name)}`;
  if (cascade) s += ` ${cascadeKeyword}`;
  return s;
}
